import { NetworkStatus, useQuery } from '@apollo/client';
import { Box } from '@ovComponents/1-primative/box/box';
import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Card } from '@ovComponents/2-component/card/card';
import {
  SnapshotCard,
  SnapshotCardGrid,
  SnapshotMetadataHeader,
} from '@ovComponents/3-pattern/snapshotCard';
import { BatchRecap } from '@ovComponents/4-module/widgets/batchRecap/batchRecap';
import { DriftQueue } from '@ovComponents/4-module/widgets/driftQueue/driftQueue';
import TradeFlowPipeline from '@ovComponents/4-module/widgets/tradeFlowPipeline/tradeFlowPipeline';
import TradeFlowPipelineTotal from '@ovComponents/4-module/widgets/tradeFlowPipeline/tradeFlowPipelineTotal';
import { WatchList } from '@ovComponents/4-module/widgets/watchList/watchList';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import {
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FETCH_DASHBOARD_STATS } from '../queries';
import { BOLD_SX, SECONDARY_TEXT_SX, SECTION_HEADER_SX } from '../styles';
import {
  type PinnedPortfolioDeepLink,
  useTradingHub,
} from '../tradingHubContext';
import type {
  BulkTradeRunState,
  DashboardPortfolioReport,
  OptimizerState,
  TabId,
} from '../types';
import {
  BULK_TRADE_RUN_STATE_LABELS,
  getBulkTradeRunProgress,
  getOptimizerProgress,
  OPTIMIZER_STATE_LABELS,
} from '../utils';
import { deriveDashboardStats } from '../utils/dashboardStats';
import { translateSnapshotCards } from '../utils/snapshotCardI18n';
import type { SnapshotCardKey } from '../utils/v2Mappers';
import {
  mapDashboardToBatchRecap,
  mapDashboardToSnapshotCards,
  mapPortfolioReportsToDriftAccounts,
} from '../utils/v2Mappers';
import OperationRow from './operationRow';

type NavigateToAccountPortfolioDeps = {
  setPinnedPortfolioDeepLink: (link: PinnedPortfolioDeepLink | null) => void;
  setCurrentTab: (tab: TabId) => void;
};

const navigateToAccountPortfolio = (
  reports: DashboardPortfolioReport[],
  account: { id: string },
  { setPinnedPortfolioDeepLink, setCurrentTab }: NavigateToAccountPortfolioDeps,
): void => {
  const report = reports.find((r) => r.id === account.id);
  if (!report) return;
  setPinnedPortfolioDeepLink({
    portfolioReportId: account.id,
    report,
  });
  setCurrentTab('portfolios');
};

/** Shared height for the side-by-side Pipeline + Activity Recap panels. */
const PANEL_HEIGHT = 440;

const TodaysActivityTabV2 = () => {
  const { t } = useTranslation('portfolioTradingHub');
  const { sys } = useThemeTokens();
  const { activeOrganization } = useContext(UserContext);
  const {
    updateStatusBarStats,
    setCurrentTab,
    setPinnedPortfolioDeepLink,
    refreshKey,
    triggerRefresh,
  } = useTradingHub();
  const [lastRefreshed, setLastRefreshed] = useState<Date>(() => new Date());

  const handleRefresh = useCallback(() => {
    triggerRefresh();
  }, [triggerRefresh]);

  const {
    loading: loadingStats,
    data: statsData,
    refetch: refetchStats,
    networkStatus,
  } = useQuery(FETCH_DASHBOARD_STATS, {
    variables: { organizationId: activeOrganization?.id },
    skip: !activeOrganization?.id,
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (refreshKey > 0) refetchStats();
  }, [refreshKey]);

  // Advance "As of" only when a fetch/refetch settles — not when refreshKey
  // bumps while cached stats are still on screen.
  useEffect(() => {
    if (networkStatus === NetworkStatus.ready && statsData) {
      setLastRefreshed(new Date());
    }
  }, [networkStatus, statsData]);

  const dashboardStats = useMemo(
    () => deriveDashboardStats(statsData),
    [statsData],
  );
  const {
    portfolioStats,
    operationsStats,
    tradeStats,
    reports,
    optimizers,
    bulkRuns,
  } = dashboardStats;

  useEffect(() => {
    if (statsData) {
      updateStatusBarStats({
        needsAction: portfolioStats.actionQueue,
        inProgress: operationsStats.totalActive,
        onTrack: portfolioStats.onTrack,
        totalAum: portfolioStats.totalValue,
        activeOperations: operationsStats.totalActive,
        tradesInFlight: tradeStats.total,
      });
    }
  }, [
    portfolioStats,
    operationsStats,
    tradeStats,
    statsData,
    updateStatusBarStats,
  ]);

  const snapshotCards = useMemo(
    () =>
      translateSnapshotCards(
        mapDashboardToSnapshotCards({
          portfolioStats,
          operationsStats: {
            totalActive: operationsStats.totalActive,
            activeOptimizers: operationsStats.activeOptimizers.length,
            activeBulkRuns: operationsStats.activeBulkRuns.length,
          },
          tradeStats,
        }),
        t,
      ),
    [portfolioStats, operationsStats, tradeStats, t],
  );

  const handleSnapshotCardClick = useCallback(
    (cardKey: SnapshotCardKey) => {
      startTransition(() => {
        if (cardKey === 'needsAction') setCurrentTab('portfolios');
        else if (cardKey === 'tradePipeline') setCurrentTab('tradeRequests');
        else if (cardKey === 'opsInFlight') setCurrentTab('operations');
      });
    },
    [setCurrentTab],
  );

  const driftAccounts = useMemo(
    () => mapPortfolioReportsToDriftAccounts(reports),
    [reports],
  );

  const handleAccountClick = useCallback(
    (account: { id: string }) => {
      navigateToAccountPortfolio(reports, account, {
        setPinnedPortfolioDeepLink,
        setCurrentTab,
      });
    },
    [reports, setPinnedPortfolioDeepLink, setCurrentTab],
  );

  const batchRecapData = useMemo(
    () => mapDashboardToBatchRecap(optimizers, bulkRuns),
    [optimizers, bulkRuns],
  );

  const tradePipelineData = useMemo(
    () => ({
      stages: [
        {
          id: 'staged',
          title: t('tradePipeline.staged.title'),
          count: tradeStats.initiated,
          description: t('tradePipeline.staged.description'),
          colorKey: 'supportOne' as const,
        },
        {
          id: 'approved',
          title: t('tradePipeline.approved.title'),
          count: tradeStats.ready,
          description: t('tradePipeline.approved.description'),
          colorKey: 'positive' as const,
        },
        {
          id: 'sent',
          title: t('tradePipeline.sent.title'),
          count: tradeStats.requested,
          description: t('tradePipeline.sent.description'),
          colorKey: 'supportTwo' as const,
        },
      ],
    }),
    [tradeStats, t],
  );

  if (loadingStats && !statsData)
    return (
      <Box minHeight='400px'>
        <Skeleton variant='text' width='40%' height={32} sx={{ mb: 0.5 }} />
        <Skeleton variant='text' width='25%' height={16} sx={{ mb: 3 }} />
        <Box display='flex' gap={2} mb={3}>
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              variant='rectangular'
              sx={{ flex: 1, height: 120, borderRadius: 1 }}
            />
          ))}
        </Box>
        <Skeleton
          variant='rectangular'
          height={280}
          sx={{ borderRadius: 1, mb: 2 }}
        />
        <Skeleton variant='rectangular' height={200} sx={{ borderRadius: 1 }} />
      </Box>
    );

  return (
    <Box>
      <SnapshotMetadataHeader
        date={lastRefreshed}
        portfolioCount={portfolioStats.total}
        aumCents={portfolioStats.totalValue}
        onRefresh={handleRefresh}
      />

      <Box mb={2.5}>
        <SnapshotCardGrid>
          {snapshotCards.map((card) => (
            <SnapshotCard
              key={card.cardKey}
              {...card}
              compact
              onClick={() => handleSnapshotCardClick(card.cardKey)}
            />
          ))}
        </SnapshotCardGrid>
      </Box>

      {/* DriftQueue lives in an elevated white Card — same chrome as the other
          cards (no gray outline). Only the table body is height-capped +
          scrollable; the widget header (title / last refreshed / stat cards)
          and footer ("showing X of Y") stay fixed. A sticky thead keeps the
          column header visible while the rows scroll. */}
      <Card sx={{ mb: 2, overflow: 'hidden' }}>
        {statsData && (
          <DriftQueue
            showLastRefreshed={false}
            accounts={driftAccounts}
            onAccountClick={handleAccountClick}
          />
        )}
      </Card>

      {/* Watch List sits directly under the Drift Queue, capped + scrollable
          with the identical chrome: elevated white Card, table body limited to
          maxHeight with a sticky header, while the widget header/footer stay
          fixed. The widget brings no horizontal padding of its own (it normally
          lives in a padded page), so inset it here to keep the title/controls
          off the card edge. */}
      <Card sx={{ mb: 2, overflow: 'hidden', px: 2 }}>
        <WatchList />
      </Card>

      {/* Pipeline + Activity Recap sit side by side, both capped to the same
          height. The inner scroll regions (active operations / event log) keep
          each panel from growing past PANEL_HEIGHT. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 2,
          mb: 2,
          alignItems: 'stretch',
        }}
      >
        <Card
          sx={{
            height: PANEL_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Trade-flow summary replaced by the shared TradeFlowPipeline
              widget. It brings its own title + "View trades" affordance, so it
              also stands in for the old "Pipeline / View Ops" header. The card
              stays pinned to PANEL_HEIGHT and Active Operations keeps its
              scroll region, so the panel never grows. */}
          <Box
            sx={{
              flexShrink: 0,
              // Match BatchRecap's header padding (p: 2) so the Pipeline and
              // Activity Recap titles/edges line up side by side.
              p: 2,
              borderBottom: '1px solid',
              borderColor: sys.color.outlineVariant,
            }}
          >
            <TradeFlowPipeline
              data={tradePipelineData}
              onNavigate={() => setCurrentTab('tradeRequests')}
            />
            <TradeFlowPipelineTotal stages={tradePipelineData.stages} />
          </Box>

          <Box
            px={2}
            py={1}
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant='labelSmall'
              sx={{ ...SECTION_HEADER_SX, mb: 0.75, flexShrink: 0 }}
            >
              {t('operations.activeTitle', {
                count: operationsStats.totalActive,
              })}
            </Typography>
            {operationsStats.totalActive > 0 ? (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                {operationsStats.activeOptimizers.map((op) => (
                  <OperationRow
                    key={op.id}
                    badgeLabel='OPT'
                    badgeColor='warning'
                    title={t('operations.optimizerPortfolios', {
                      count: op.numPortfolios,
                    })}
                    progress={getOptimizerProgress(op.state as OptimizerState)}
                    stateLabel={
                      OPTIMIZER_STATE_LABELS[op.state as OptimizerState] ||
                      op.state
                    }
                    elapsed={Math.round(
                      (Date.now() - new Date(op.createdAt).getTime()) / 60000,
                    )}
                    accentColor={sys.color.warning}
                  />
                ))}
                {operationsStats.activeBulkRuns.map((run) => (
                  <OperationRow
                    key={run.id}
                    badgeLabel='BATCH'
                    badgeColor='neutral'
                    title={t('operations.bulkTradeRun')}
                    progress={getBulkTradeRunProgress(
                      run.state as BulkTradeRunState,
                    )}
                    stateLabel={
                      BULK_TRADE_RUN_STATE_LABELS[
                        run.state as BulkTradeRunState
                      ] || run.state
                    }
                    elapsed={Math.round(
                      (Date.now() - new Date(run.createdAt).getTime()) / 60000,
                    )}
                    accentColor={sys.color.primary}
                    progressBarColor='info'
                  />
                ))}
              </Box>
            ) : (
              <Box
                py={3}
                textAlign='center'
                sx={{
                  minHeight: 80,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant='labelSmall'
                  sx={{ color: sys.color.positive, ...BOLD_SX, mb: 0.25 }}
                >
                  {t('operations.emptyTitle')}
                </Typography>
                <Typography
                  variant='labelSmall'
                  sx={{ ...SECONDARY_TEXT_SX, fontSize: '0.65rem' }}
                >
                  {t('operations.emptyDescription')}
                </Typography>
              </Box>
            )}
          </Box>
        </Card>

        {/* Activity Recap is capped to the same height as Pipeline. BatchRecap
            keeps its header / summary / filters fixed and scrolls only the
            event-log table internally. */}
        <Box
          sx={{
            height: PANEL_HEIGHT,
            '& > .MuiCard-root': { height: '100%' },
          }}
        >
          <BatchRecap data={batchRecapData} asOfVariant='inline' />
        </Box>
      </Box>
    </Box>
  );
};

export default TodaysActivityTabV2;
